import { Transition } from '@headlessui/react';
import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import cn from 'clsx';
import { Fragment, useMemo, useState } from 'react';
import {
  NotificationDuration,
  NotificationEnums,
  NotificationPosition,
  Theme,
  themeMap,
} from 'src/lib/useNotify';
import { isOTT as isOTTFn } from 'src/lib/utilities';
import { Button, Loader } from 'src/shared/core/ui';

import ExclamationCircleIcon2 from './ExclamationCircleIcon2';
import SuccessCircleIcon from './SuccessCircleIcon';
import WarningIcon from './WarningIcon';

// Sample code for notification
// <Button onClick={() => {
//     showNotification({
//         description: "Description  " + new Date().getTime(),
//         type: NotificationEnums.ERROR,
//         title: "New Title",
//         onClose: () => {
//             console.log('closed');
//         },
//         content: <div className={'bg-pink-600'}>
//             Error
//         </div>
//     })
// }}>
//     Hllo
// </Button>
export default function SuccessNotification({
  index,
  type,
  totalCount,
  content,
  title,
  description,
  themeSelected,
  onClose,
  position = NotificationPosition.TOP_RIGHT,
  actionLabel,
  onAction,
  // duration = NotificationDuration.NONE
}: {
  index: number;
  totalCount: number;
  title?: string;
  content?: any;
  description?: string;
  type: NotificationEnums;
  themeSelected?: Theme;
  position?: NotificationPosition;
  onClose: () => void;
  duration?: NotificationDuration;
  actionLabel?: string;
  onAction?: () => void;
}) {
  const [show, setShow] = useState(true);

  // Check if OTT mode
  const isOTT = isOTTFn();

  // Override theme to OTT if vType is OTT, otherwise use the provided theme
  const theme = isOTT ? Theme.OTT : themeSelected || Theme.LIGHT;

  const positionStyles = {
    [NotificationPosition.TOP_LEFT]: {
      verticalPosition: 'sm:items-start px-4 sm:p-6',
      horizontalPosition: 'sm:items-start',
      transitionClasses: 'transform ease-out duration-300 transition',
    },
    [NotificationPosition.TOP_CENTER]: {
      verticalPosition: 'sm:items-start px-4 sm:px-0',
      horizontalPosition: 'sm:items-center',
      transitionClasses: 'transform ease-out duration-300 transition',
    },
    [NotificationPosition.TOP_RIGHT]: {
      verticalPosition: 'sm:items-start px-4 sm:p-6',
      horizontalPosition: 'sm:items-end',
      transitionClasses: 'transform ease-out duration-300 transition',
    },
    [NotificationPosition.BOTTOM_LEFT]: {
      verticalPosition: 'sm:items-end px-4 sm:p-6',
      horizontalPosition: 'sm:items-start',
      transitionClasses: 'transform ease-in duration-100',
    },
    [NotificationPosition.BOTTOM_CENTER]: {
      verticalPosition: 'sm:items-end px-4 sm:px-0',
      horizontalPosition: 'sm:items-center',
      transitionClasses: 'transform ease-in duration-100',
    },
    [NotificationPosition.BOTTOM_RIGHT]: {
      verticalPosition: 'sm:items-end px-4 sm:p-6',
      horizontalPosition: 'sm:items-end',
      transitionClasses: 'transform ease-in duration-100',
    },
    [NotificationPosition.BOTTOM_LEFT_ABOVE_FOOTER]: {
      verticalPosition: 'sm:items-end px-4 sm:p-6 sm:pb-[56px]', // 40px footer + 16px gap
      horizontalPosition: 'sm:items-start',
      transitionClasses: 'transform ease-in duration-100',
    },
    // Default styles
    default: {
      verticalPosition: 'sm:items-start',
      horizontalPosition: 'sm:items-end',
      transitionClasses: 'transform ease-out duration-300 transition',
    },
  };
  const { backgroundColor, textColor } = themeMap[theme] || themeMap[Theme.LIGHT];
  const { verticalPosition, horizontalPosition, transitionClasses } =
    positionStyles[position] || positionStyles.default;

  return (
    <>
      {/* Global notification live region, render this permanently at the end of the document */}
      <div
        aria-live='assertive'
        className={cn(
          'pointer-events-none fixed inset-0 flex items-end px-4 py-6 z-[99999] sm:items-start sm:p-6',
          verticalPosition,
        )}
      >
        <div
          className={cn(
            'flex w-full flex-col items-center space-y-4 sm:items-end',
            horizontalPosition,
          )}
        >
          {/* Notification panel, dynamically insert this into the live region when it needs to be displayed */}
          <Transition
            show={show}
            as={Fragment}
            // enter="transform ease-out duration-300 transition"
            enter={transitionClasses}
            enterFrom='translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2'
            enterTo='translate-y-0 opacity-100 sm:translate-x-0'
            leave='transition ease-in duration-100'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <div
              className={cn(
                'pointer-events-auto  w-full transition-all duration-300 animate-notificationZoom animated max-w-sm overflow-hidden rounded-lg  shadow-lg ring-1 ring-black ring-opacity-5',
                {
                  ['  ']: index === totalCount - 1,
                },
                backgroundColor,
              )}
              style={{
                transform: `translateY(${
                  (totalCount - index - 1) * 5
                }px) scale(${(100 - (totalCount - index - 1) * 4) / 100})`,
              }}
            >
              <div className={cn(isOTT ? 'p-[12px]' : 'p-4')}>
                <div className='flex items-start'>
                  <div className='flex-shrink-0'>
                    {type === NotificationEnums.LOADING && <Loader scale={1} />}
                    {type === NotificationEnums.SUCCESS &&
                      (isOTT ? (
                        <SuccessCircleIcon className='h-5 w-5' />
                      ) : (
                        <CheckCircleIcon className='h-6 w-6 text-green-400' aria-hidden='true' />
                      ))}
                    {type === NotificationEnums.ERROR &&
                      (isOTT ? (
                        <ExclamationCircleIcon2 className='h-5 w-5' />
                      ) : (
                        <ExclamationCircleIcon
                          className='h-6 w-6 text-red-400'
                          aria-hidden='true'
                        />
                      ))}
                    {type === NotificationEnums.WARNING &&
                      (isOTT ? (
                        <WarningIcon className='h-5 w-5' />
                      ) : (
                        <ExclamationCircleIcon
                          className='h-6 w-6 text-yellow-600'
                          aria-hidden='true'
                        />
                      ))}
                    {type === NotificationEnums.INFO && (
                      <InformationCircleIcon className='h-6 w-6 text-blue-400' aria-hidden='true' />
                    )}
                  </div>
                  <div className='ml-3 w-0 flex-1 pt-0.5'>
                    {title && (
                      <p
                        className={cn(
                          'text-sm font-medium',
                          isOTT ? 'text-[#FFFFFF]' : cn('text-gray-900', textColor),
                        )}
                      >
                        {title}
                      </p>
                    )}
                    {description && (
                      <p
                        className={cn(
                          'mt-1 text-sm',
                          isOTT ? 'text-[#FFFFFF]' : cn('text-gray-500', textColor),
                          onAction ? 'cursor-pointer underline hover:opacity-80 transition' : '',
                        )}
                        onClick={
                          onAction
                            ? () => {
                                onAction();
                                onClose();
                              }
                            : undefined
                        }
                      >
                        {description}
                      </p>
                    )}
                    {content}
                    {actionLabel && onAction && (
                      <div className='mt-3'>
                        <Button
                          variant='secondary'
                          onClick={() => {
                            onAction();
                            onClose();
                          }}
                          className='text-sm'
                        >
                          {actionLabel}
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className='ml-4 flex flex-shrink-0'>
                    <Button variant={'naked'} onClick={onClose}>
                      <XMarkIcon
                        className={cn('h-5 w-5', isOTT ? 'text-[#FFFFFF]' : textColor)}
                        aria-hidden='true'
                      />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </>
  );
}
